/**
 * Metro area groupings for US cities
 * Maps metro area names to arrays of city names
 */
export const METRO_AREA_MAPPINGS: Record<string, string[]> = {
	"San Francisco Bay Area": [
		"Alameda",
		"Oakland",
		"San Francisco",
		"San Jose",
		"Berkeley",
		"Palo Alto",
		"Mountain View",
		"Sunnyvale",
		"Fremont",
		"Santa Clara",
	],
	"Greater Los Angeles": [
		"Los Angeles",
		"Inglewood",
		"Costa Mesa",
		"Anaheim",
		"Long Beach",
		"Pasadena",
		"Santa Monica",
		"Burbank",
		"Glendale",
		"Irvine",
	],
	"Denver Metro": [
		"Denver",
		"Morrison",
		"Aurora",
		"Boulder",
		"Lakewood",
		"Arvada",
	],
	"Washington DC Metro": [
		"Washington",
		"Arlington",
		"Alexandria",
		"Bethesda",
		"Silver Spring",
	],
	"Miami Metro": [
		"Miami",
		"Fort Lauderdale",
		"Miami Beach",
		"Hollywood",
		"West Palm Beach",
	],
	"Seattle Metro": [
		"Seattle",
		"Tacoma",
		"Bellevue",
		"Redmond",
	],
	"San Diego Metro": [
		"San Diego",
		"Chula Vista",
		"Oceanside",
	],
};

export interface LocationGroup {
	type: "metro" | "standalone";
	metroArea?: string;
	cities: string[];
}

/**
 * Groups cities by metro area
 * Returns array of LocationGroups with metro areas and standalone cities
 */
export function groupCitiesByMetroArea(cities: string[]): LocationGroup[] {
	const groups: LocationGroup[] = [];
	const assignedCities = new Set<string>();

	// Group cities by metro area
	for (const [metroArea, metroCities] of Object.entries(METRO_AREA_MAPPINGS)) {
		const matchingCities = cities.filter((cityWithState) => {
			const cityName = cityWithState.split(",")[0]?.trim();
			return cityName && metroCities.includes(cityName);
		});

		if (matchingCities.length > 0) {
			groups.push({
				type: "metro",
				metroArea,
				cities: matchingCities.sort(),
			});
			matchingCities.forEach((city) => assignedCities.add(city));
		}
	}

	// Add standalone cities (not in any metro area)
	const standaloneCities = cities
		.filter((city) => !assignedCities.has(city))
		.sort();

	for (const city of standaloneCities) {
		groups.push({
			type: "standalone",
			cities: [city],
		});
	}

	return groups;
}

/**
 * Flattens grouped locations back to a simple array
 * Useful for backward compatibility
 */
export function flattenLocationGroups(groups: LocationGroup[]): string[] {
	return groups.flatMap((group) => group.cities);
}
